/**
 * المرفقات.
 *
 * العقد الموقَّع وفاتورة شراء الأصل وبطاقة الموظف لم يكن لها مكان في النظام
 * كله — كان الحقل رابطًا نصيًا يُلصق فيه عنوان من خدمة أخرى.
 */

/* ==========================================================================
   ما يُقبل
   ========================================================================== */

export interface FileTypeDef {
  mime: string;
  ext: readonly string[];
  label: string;
}

/**
 * قائمة بيضاء لا سوداء.
 *
 * القائمة السوداء تُنسى: يظهر امتداد خطر جديد فيمرّ لأنه ليس فيها. البيضاء
 * تمنع كل ما لم يُذكر صراحةً، فالخطأ فيها يمنع ملفًا مشروعًا لا يفتح ثغرة.
 *
 * والغائب عنها عمدًا: SVG يحمل سكربتًا يعمل عند العرض، وHTML يُقدَّم من
 * نطاقنا فيصير حقنًا في صفحاتنا، والتنفيذيات لا شأن لنظام إدارة بها.
 */
export const ALLOWED_FILE_TYPES: readonly FileTypeDef[] = [
  { mime: 'application/pdf', ext: ['pdf'], label: 'PDF' },
  { mime: 'image/jpeg', ext: ['jpg', 'jpeg'], label: 'صورة JPEG' },
  { mime: 'image/png', ext: ['png'], label: 'صورة PNG' },
  { mime: 'image/webp', ext: ['webp'], label: 'صورة WebP' },
  { mime: 'image/gif', ext: ['gif'], label: 'صورة GIF' },
  {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ext: ['docx'],
    label: 'مستند Word',
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ext: ['xlsx'],
    label: 'جدول Excel',
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ext: ['pptx'],
    label: 'عرض PowerPoint',
  },
  { mime: 'application/msword', ext: ['doc'], label: 'مستند Word قديم' },
  { mime: 'application/vnd.ms-excel', ext: ['xls'], label: 'جدول Excel قديم' },
  { mime: 'text/csv', ext: ['csv'], label: 'ملف CSV' },
  { mime: 'text/plain', ext: ['txt'], label: 'نص' },
  { mime: 'application/zip', ext: ['zip'], label: 'أرشيف ZIP' },
  /*
   * الصوت.
   *
   * `webm` أولًا لأنه ما يُنتجه تسجيل المتصفح، والباقي لما يُرفَع من جهاز.
   * ولا فيديو: حدّ الملف عشرة ميجابايت، وأي مقطع ذي معنى يتجاوزه — فإتاحته
   * تعني رفضًا عند كل محاولة، وهو أسوأ من منعٍ مُعلن.
   */
  { mime: 'audio/webm', ext: ['webm'], label: 'تسجيل صوتي' },
  { mime: 'audio/mpeg', ext: ['mp3'], label: 'صوت MP3' },
  { mime: 'audio/mp4', ext: ['m4a'], label: 'صوت M4A' },
  { mime: 'audio/wav', ext: ['wav'], label: 'صوت WAV' },
] as const;

export const ALLOWED_MIME = new Set(ALLOWED_FILE_TYPES.map((t) => t.mime));
export const ALLOWED_EXT = new Set(ALLOWED_FILE_TYPES.flatMap((t) => t.ext));

/** ما يُعرض في حقل الاختيار — يقصر نافذة الملفات على المقبول. */
export const ACCEPT_ATTRIBUTE = ALLOWED_FILE_TYPES.flatMap((t) =>
  t.ext.map((e) => `.${e}`),
).join(',');

/** أقصى حجم للملف الواحد: عشرة ميجابايت. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} بايت`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} كيلوبايت`;
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} ميجابايت`;
}

/* ==========================================================================
   الكيانات التي تقبل مرفقات
   ========================================================================== */

export interface AttachableDef {
  key: string;
  label: string;
  /** أدنى دور يرفق ويحذف. */
  minRole: 'EMPLOYEE' | 'SALES' | 'MANAGER' | 'ADMIN';
  /**
   * دورٌ أدنى يرى ويرفق على ما سجّله هو وحده.
   *
   * المندوب يحصّل من العميل ويرفع إيصاله، ولا يرى سندات تحصيلات غيره.
   */
  ownRole?: 'EMPLOYEE' | 'SALES' | 'MANAGER';
  /** ما يُقترح إرفاقه — يظهر في الشاشة تلميحًا. */
  hint: string;
}

/**
 * الكيانات المسموح الإرفاق عليها.
 *
 * قائمة مغلقة كسائر الكتالوجات: بدونها يستطيع من يُرسل طلبًا مباشرًا أن
 * يعلّق ملفات على أي جدول باسمه، فيصير المرفق مساحة تخزين حرّة بلا حساب.
 */
export const ATTACHABLE: readonly AttachableDef[] = [
  { key: 'account', label: 'العميل', minRole: 'SALES', hint: 'السجل التجاري، البطاقة الضريبية' },
  { key: 'lead', label: 'العميل المحتمل', minRole: 'SALES', hint: 'كارت، عرض وارد' },
  { key: 'deal', label: 'الصفقة', minRole: 'SALES', hint: 'كراسة شروط، مراسلات' },
  { key: 'quotation', label: 'عرض السعر', minRole: 'SALES', hint: 'العرض الموقَّع' },
  { key: 'contract', label: 'العقد', minRole: 'SALES', hint: 'العقد الموقَّع والملاحق' },
  { key: 'invoice', label: 'الفاتورة', minRole: 'MANAGER', hint: 'الفاتورة الضريبية، إذن التسليم' },
  { key: 'payment', label: 'التحصيل', minRole: 'MANAGER', ownRole: 'SALES', hint: 'إشعار التحويل، صورة الشيك' },
  { key: 'expense', label: 'المصروف', minRole: 'MANAGER', hint: 'الفاتورة، الإيصال' },
  { key: 'bill', label: 'فاتورة المورد', minRole: 'MANAGER', hint: 'فاتورة المورد' },
  /*
   * القيد اليومي.
   *
   * معظم تاريخ العملاء دخل قيودًا من الجدول القديم بلا فواتير، فسند القيد
   * — إيصالٌ أو صورة تحويل — لا مكان له إلا على القيد نفسه.
   */
  { key: 'journalEntry', label: 'القيد', minRole: 'MANAGER', hint: 'سند القيد، إيصال، صورة تحويل' },
  { key: 'employee', label: 'الموظف', minRole: 'ADMIN', hint: 'العقد، المؤهل، بطاقة الرقم القومي' },
  { key: 'asset', label: 'الأصل', minRole: 'MANAGER', hint: 'فاتورة الشراء، الضمان' },
  { key: 'activity', label: 'النشاط', minRole: 'SALES', hint: 'محضر اجتماع، مرفق مكالمة' },
  /*
   * جهة الاتصال والتغذية الراجعة.
   *
   * الأولى كانت غائبة سهوًا — لها مرفقات كغيرها. والثانية موضع التسجيل
   * الصوتي: يُرفع مرفقًا عليها ثم تُشير إليه، فلا يُعاد بناء حدّ الحجم
   * وحدّ الباقة وكشف التكرار في جدول آخر.
   */
  { key: 'contact', label: 'جهة الاتصال', minRole: 'SALES', hint: 'كارت، مستند تعريف' },
  { key: 'feedback', label: 'التغذية الراجعة', minRole: 'EMPLOYEE', hint: 'تسجيل صوتي' },
  /* تسجيل المكالمة يُرفع مرفقًا ثم تشير إليه المكالمة — كالتغذية الراجعة سواء. */
  { key: 'call', label: 'المكالمة', minRole: 'SALES', hint: 'تسجيل المكالمة' },
  /* مهام الوكالة وبريف الانضمام: التنفيذ اليومي يرفع تصاميم ومستندات عمل. */
  { key: 'task', label: 'مهمة الوكالة', minRole: 'EMPLOYEE', hint: 'تصميم، ملف عمل، لقطة' },
  { key: 'onboarding', label: 'بريف الانضمام', minRole: 'EMPLOYEE', hint: 'هوية، أدلة، ملفات العميل' },
] as const;

export const ATTACHABLE_MAP = Object.fromEntries(
  ATTACHABLE.map((a) => [a.key, a]),
) as Readonly<Record<string, AttachableDef>>;

/* ==========================================================================
   اسم الملف
   ========================================================================== */

/**
 * تنقية اسم الملف.
 *
 * الاسم يعود في ترويسة Content-Disposition وفي اسم ما يُحفظ على جهاز
 * المستخدم. المسارات فيه — «../» أو الشرطة المقلوبة — تخرج من المجلد
 * المقصود، والأسطر الجديدة تحقن ترويسات إضافية في الاستجابة.
 *
 * العربية تبقى كما هي: قصر الاسم على اللاتينية يجعل «عقد شركة النور.pdf»
 * يصل باسم مشوّه أو فارغ.
 */
export function safeFilename(raw: string): string {
  const base = raw
    .replace(/[\\/]/g, ' ')
    // أحرف التحكّم — ومنها السطر الجديد الذي يحقن ترويسة ثانية في الاستجابة.
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\.{2,}/g, '.')
    .trim();

  const cleaned = base.replace(/^\.+/, '').slice(0, 180);
  return cleaned || 'ملف';
}

export function extensionOf(filename: string): string {
  const i = filename.lastIndexOf('.');
  return i > 0 ? filename.slice(i + 1).toLowerCase() : '';
}

/**
 * هل النوع مقبول.
 *
 * الامتداد والنوع المُعلَن معًا: المتصفح قد يُرسل نوعًا عامًا لملف صحيح،
 * وقد يُرسل نوعًا مقبولًا لملف امتداده خطر. الاتفاق بينهما شرط.
 */
export function isAllowed(filename: string, mime: string): boolean {
  const ext = extensionOf(filename);
  if (!ALLOWED_EXT.has(ext)) return false;

  const def = ALLOWED_FILE_TYPES.find((t) => t.ext.includes(ext));
  return def ? def.mime === mime : false;
}

/** وصف مختصر لأنواع الملفات المقبولة — يُعرض في الشاشة. */
export const ALLOWED_SUMMARY = 'PDF، صور، تسجيلات صوتية، Word، Excel، PowerPoint، CSV، ZIP';

/* ==========================================================================
   المعاينة
   ========================================================================== */

/**
 * صنف الملف لغرض العرض.
 *
 * التقسيم بالسلوك لا بالنوع: ما يُعرض صورةً، وما يُفتح في عارض المتصفح،
 * وما يُشغَّل، وما لا يُعاين فيُنزَّل. اشتقاقه من `mimeType` في مكان واحد
 * يمنع كل شاشة من كتابة سلسلة `startsWith` خاصة بها تختلف عن جارتها.
 */
export type AttachmentKind = 'image' | 'pdf' | 'audio' | 'other';

export function attachmentKind(mime: string): AttachmentKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('audio/')) return 'audio';
  return 'other';
}

/** الاسم العربي لنوع الملف، أو الامتداد حين لا يُعرف. */
export function fileTypeLabel(filename: string, mime: string): string {
  const byMime = ALLOWED_FILE_TYPES.find((t) => t.mime === mime);
  if (byMime) return byMime.label;

  const ext = extensionOf(filename);
  return ext ? ext.toUpperCase() : 'ملف';
}
